/**
 * @file src/hooks/useVersionInfo.ts
 * @fileoverview Definieert een hook voor het ophalen van versie-informatie van de applicatie.
 * De informatie wordt uit `footer-version.json` gehaald en gecached om onnodige fetches te voorkomen.
 */
import { useEffect, useState } from 'react';
import type { AppEnv } from '../utils/appEnv';

/**
 * Interface voor de versie-informatie van de applicatie.
 */
export interface VersionInfo {
  /** Het versienummer (bijv. "1.2.3"). */
  version: string;
  /** Het buildnummer. */
  buildNumber: number;
  /** De commit hash van de build. */
  commitHash: string;
  /** Exacte build timestamp. */
  buildTimestamp?: string;
  /** De datum van de laatste build. */
  lastBuildDate: string;
  /** Het type build (bijv. "git"). */
  buildType: string;
  /** The API endpoint URL. */
  api: string;
  /** The active application environment. */
  appEnv?: AppEnv;
  /** Whether the app is in maintenance mode. */
  isMaintenance: boolean;
}

/**
 * Een globale cache voor de versie-informatie om te voorkomen dat deze bij elke aanroep opnieuw wordt opgehaald.
 */
let cache: VersionInfo | null = null;

/**
 * Een custom hook die de versie-informatie van de applicatie ophaalt uit `public/footer-version.json`.
 * De data wordt gecached na de eerste succesvolle fetch.
 *
 * @returns {VersionInfo | null} De versie-informatie, of `null` als deze nog niet is geladen.
 */
export const useVersionInfo = () => {
  const [info, setInfo] = useState<VersionInfo | null>(cache);

  useEffect(() => {
    if (cache) {
      return;
    }
    fetch('/footer-version.json', { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        cache = data;
        setInfo(data);
      })
      .catch(() => {});
  }, []);

  return info;
};
